import { lineDrawing, type Drawing } from './picture-tiles'
import { clear, el, money, parseMoney } from './ui'
import { currencySymbol } from './round-config'

// Extras for this visit (0087; Liam, 26 Oct 2026, booking Sarah with a conservatory roof at £15 a panel: "I don't know if
// we need an extras box", then design B folded away, "75% of the time extras arent needed";
// https://claude.ai/artifact/4e32Mq9mZFU5fGGTxV3Vfh). One-off work on one visit, inside its whole price
// (amount_pence); never the customer's regular price or the next visit's. The phone's copy of functions/lib/extras.ts.

import { extraPence, extrasOf, extrasTotal, extraWork, extraWords, type Extra, type ExtraKind } from './extras-data'
export { extraPence, extrasOf, extrasTotal, extrasTotalOf, extraWork, extraWords, type Extra, type ExtraKind } from './extras-data'

const PICKS: { kind: ExtraKind; name: string; line: string; drawing: Drawing; each: number | null }[] = [
  { kind: 'conservatory', name: 'Conservatory roof', line: 'A price a panel', drawing: 'house', each: 1500 },
  { kind: 'gutters', name: 'Gutters', line: 'Add a price', drawing: 'note', each: null },
  { kind: 'other', name: 'Something else', line: 'Name and price', drawing: 'pencil', each: null },
]

export interface ExtrasBox { el: HTMLElement; value: () => Extra[]; problem: () => string | null; total: () => number }

// Folded on most visits (one dashed row); opened by a tap, or by itself when the visit already has extras. Each extra:
// its name (Something else's typed), − count + where it has a count, its price each, and × to take it off.
export function extrasBox(initial: Extra[], changed: () => void): ExtrasBox {
  const list: (Extra & { priceText: string })[] = initial.map((e) => ({ ...e, priceText: e.each_pence === null ? '' : (e.each_pence / 100).toFixed(2) }))
  let open = list.length > 0
  const root = el('section', { class: 'booking-extras', 'aria-label': 'Extras for this visit' })
  const draw = (): void => {
    clear(root)
    if (!open) {
      const fold = el('button', { type: 'button', class: 'booking-extras-fold', 'aria-expanded': 'false' },
        el('span', { class: 'booking-extras-ring', 'aria-hidden': 'true' }, lineDrawing('calendarPlus', 18)),
        el('span', { class: 'booking-extras-words' }, el('strong', {}, 'Add an extra for this visit'), el('span', {}, 'Conservatory roof, gutters, something else')),
        lineDrawing('chevronDown', 18))
      fold.addEventListener('click', () => { open = true; draw() })
      root.append(fold)
      return
    }
    root.append(el('p', { class: 'booking-extras-head' }, 'Extras for this visit'))
    const rows = el('div', { class: 'booking-extras-rows' })
    list.forEach((e, i) => {
      const name = el('input', { type: 'text', maxlength: '60', 'aria-label': 'What it is', placeholder: 'What it is' }) as HTMLInputElement
      name.value = e.kind === 'other' && e.name === 'Something else' ? '' : e.name
      name.readOnly = e.kind !== 'other'
      name.addEventListener('input', () => { e.name = name.value; changed() })
      const each = el('input', { inputmode: 'decimal', 'aria-label': `${e.name} price${e.kind === 'conservatory' ? ' a panel' : ''}`, placeholder: e.kind === 'conservatory' ? '15' : 'Price' }) as HTMLInputElement
      each.value = e.priceText
      each.addEventListener('input', () => { e.priceText = each.value; changed(); sum.textContent = rowSum(e) })
      const sum = el('strong', { class: 'booking-extra-sum' }, rowSum(e))
      const minus = el('button', { type: 'button', class: 'booking-extra-step', 'aria-label': 'One fewer' }, '−')
      const plus = el('button', { type: 'button', class: 'booking-extra-step', 'aria-label': 'One more' }, '+')
      const count = el('span', { class: 'booking-extra-count' }, `${e.count}`)
      minus.addEventListener('click', () => { if (e.count > 1) { e.count -= 1; count.textContent = `${e.count}`; sum.textContent = rowSum(e); changed() } })
      plus.addEventListener('click', () => { if (e.count < 99) { e.count += 1; count.textContent = `${e.count}`; sum.textContent = rowSum(e); changed() } })
      const remove = el('button', { type: 'button', class: 'booking-extra-remove', 'aria-label': `Take ${e.name} off` }, '×')
      remove.addEventListener('click', () => { list.splice(i, 1); if (!list.length) open = false; draw(); changed() })
      rows.append(el('div', { class: 'booking-extra' },
        el('div', { class: 'booking-extra-top' }, name, remove),
        el('div', { class: 'booking-extra-bottom' },
          el('label', { class: 'booking-extra-each' }, el('span', {}, `${currencySymbol()} ${e.kind === 'conservatory' ? 'a panel' : 'each'}`), each),
          el('span', { class: 'booking-extra-counter' }, minus, count, plus), sum)))
    })
    root.append(rows)
    const tiles = el('div', { class: 'booking-extras-tiles' })
    for (const p of PICKS) {
      const t = el('button', { type: 'button', class: 'booking-extras-tile' },
        el('span', { class: 'booking-extras-ring', 'aria-hidden': 'true' }, lineDrawing(p.drawing, 18)),
        el('span', { class: 'booking-extras-words' }, el('strong', {}, p.name), el('span', {}, p.line)))
      t.addEventListener('click', () => {
        list.push({ kind: p.kind, name: p.name, count: 1, each_pence: p.each, priceText: p.each === null ? '' : (p.each / 100).toFixed(2) })
        draw(); changed()
        const boxes = root.querySelectorAll<HTMLInputElement>('.booking-extra input')
        const last = p.kind === 'other' ? boxes[boxes.length - 2] : boxes[boxes.length - 1]
        last?.focus?.()
      })
      tiles.append(t)
    }
    root.append(tiles)
    if (!list.length) {
      const fold = el('button', { type: 'button', class: 'link-button' }, 'No extras')
      fold.addEventListener('click', () => { open = false; draw() })
      root.append(fold)
    }
  }
  const rowSum = (e: Extra & { priceText: string }): string => {
    try { const p = parseMoney(e.priceText); return p === null ? '' : money(p * e.count) } catch { return '' }
  }
  const value = (): Extra[] => list.map((e) => {
    let each: number | null = null
    try { each = parseMoney(e.priceText) } catch { each = null }
    return { kind: e.kind, name: e.name.trim(), count: e.count, each_pence: each }
  })
  draw()
  return {
    el: root,
    value,
    total: () => extrasTotal(value()),
    problem: () => {
      for (const e of list) {
        if (!e.name.trim() || (e.kind === 'other' && e.name === 'Something else')) return 'Say what the extra is, or take it off.'
        try { if (parseMoney(e.priceText) === null) return `Put in the price for ${e.name}, or take it off.` } catch (err) { return (err as Error).message }
      }
      return null
    },
  }
}

// On a card or the visit's details: "+ Conservatory roof · 4 panels £60" badges, no price for a phone with prices hidden.
export function extraBadges(json: string | null | undefined, withPrice = true): HTMLElement[] {
  return extrasOf(json).map((e) => el('span', { class: 'badge badge-extra' }, `+ ${withPrice ? extraWords(e) : extraWork(e)}`))
}

// A helper's extras waiting for the owner's price (0091): "+ Gutters · waiting for a price", dashed, on the job for
// both of them until the owner charges it or not (src/worker-notes.ts).
export function askedBadges(json: string | null | undefined): HTMLElement[] {
  let list: { kind?: string; name?: string; count?: number }[] = []
  try { const v = json ? JSON.parse(json) : []; list = Array.isArray(v) ? v : [] } catch { list = [] }
  return list.filter((x) => x && typeof x.name === 'string').map((x) => el('span', { class: 'badge badge-extra-asked' },
    `+ ${x.kind === 'conservatory' && (x.count ?? 1) > 1 ? `${x.name} · ${x.count} panels` : x.name} · waiting for a price`))
}
